/**
 * Fiches vocabulaire : mot, auteur, livre, définition, extrait, mots cachés.
 * Même contrat que les notes : texte brut, jamais exécutable.
 */

import {
  asPlainString,
  getUtf8Bytes,
  sanitizeMultiline,
  sanitizeSingleLine,
  visibleLength,
} from "./plainTextSanitize";

export const WORD_MOT_MIN_VISIBLE = 3;
export const WORD_MOT_MAX_LENGTH = 80;
export const WORD_LABEL_MIN_VISIBLE = 3;
export const WORD_LABEL_MAX_LENGTH = 160;
export const WORD_LIVRE_MAX_LENGTH = 53;
export const WORD_AUTEUR_MAX_LENGTH = 50;
export const WORD_TEXT_MIN_VISIBLE = 10;
export const WORD_RICH_TEXT_MAX_LENGTH = 1000;
export const WORD_DEFINITION_MAX_BYTES = 20 * 1024;
export const WORD_EXCERPT_MAX_BYTES = 40 * 1024;
export const WORD_CACHE_MAX_LENGTH = 80;
export const WORD_CACHES_MIN = 1;
export const WORD_CACHES_MAX = 6;
export { visibleLength };

const WORD_MOT_RE =
  /^[a-zàâäæçéèêëïîôœùûüÿ]+(?:['’\- ][a-zàâäæçéèêëïîôœùûüÿ]+)*$/i;

export function isReservedMot(value) {
  return asPlainString(value).trim().startsWith("__");
}

export function sanitizeMotKey(raw) {
  const original = asPlainString(raw).trim();
  if (isReservedMot(original)) return original;
  return sanitizeSingleLine(raw).toLowerCase();
}

export function filterTypedWord(raw, maxLength = WORD_MOT_MAX_LENGTH) {
  return String(raw ?? "")
    .replace(/[0-9]/g, "")
    .slice(0, Math.max(0, Number(maxLength) || WORD_MOT_MAX_LENGTH));
}

export function sanitizeWordLabel(raw) {
  return sanitizeSingleLine(raw);
}

export function sanitizeWordRichText(raw) {
  return sanitizeMultiline(raw);
}

export function sanitizeMotsCaches(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  const result = [];
  for (const entry of list) {
    const value = sanitizeMotKey(entry);
    if (!value || isReservedMot(value) || value.length > WORD_CACHE_MAX_LENGTH) {
      continue;
    }
    if (visibleLength(value) < 1) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    result.push(value);
    if (result.length >= WORD_CACHES_MAX) break;
  }
  return result;
}

export function motCacheAlreadyUsed(list, index, raw) {
  const value = sanitizeMotKey(raw);
  if (!value) return false;
  const rows = Array.isArray(list) ? list : [];
  return rows.some(
    (entry, rowIndex) => rowIndex !== index && sanitizeMotKey(entry) === value
  );
}

export function isBaseMotCache(entry, mot) {
  const base = sanitizeMotKey(mot);
  return Boolean(base) && sanitizeMotKey(entry) === base;
}

export function syncBaseMotCache(list, mot, previousMot = "") {
  const base = sanitizeMotKey(mot);
  const prevBase = sanitizeMotKey(previousMot);
  const rows = Array.isArray(list) ? list : [];

  if (!base) {
    return rows.length ? rows : [""];
  }

  if (rows.some((entry) => sanitizeMotKey(entry) === base)) {
    return rows.length ? rows : [base];
  }

  const next = [...rows];
  const prevIndex = prevBase
    ? next.findIndex((entry) => sanitizeMotKey(entry) === prevBase)
    : -1;
  if (prevIndex >= 0) {
    next[prevIndex] = base;
    return next;
  }

  const rest = next.filter((entry) => String(entry).trim());
  return [base, ...rest].slice(0, WORD_CACHES_MAX);
}

export function validateWordMot(raw) {
  const value = sanitizeMotKey(raw);
  if (isReservedMot(value) || !value || !WORD_MOT_RE.test(value)) {
    return "Le mot est invalide.";
  }
  if (visibleLength(value) < WORD_MOT_MIN_VISIBLE) {
    return "Le mot doit contenir au moins 3 caractères non vides.";
  }
  if (value.length > WORD_MOT_MAX_LENGTH) {
    return `Le mot ne peut pas dépasser ${WORD_MOT_MAX_LENGTH} caractères.`;
  }
  return null;
}

export function validateWordLabel(raw, label, maxLength = WORD_LABEL_MAX_LENGTH) {
  const value = sanitizeWordLabel(raw);
  if (visibleLength(value) < WORD_LABEL_MIN_VISIBLE) {
    return `Le nom ${label} doit contenir au moins ${WORD_LABEL_MIN_VISIBLE} caractères non vides.`;
  }
  if (value.length > maxLength) {
    return `Le nom ${label} ne peut pas dépasser ${maxLength} caractères.`;
  }
  return null;
}

export function validateWordRichText(raw, label, maxBytes) {
  const value = sanitizeWordRichText(raw);
  if (visibleLength(value) < WORD_TEXT_MIN_VISIBLE) {
    return `${label} doit contenir au moins ${WORD_TEXT_MIN_VISIBLE} caractères non vides.`;
  }
  if (value.length > WORD_RICH_TEXT_MAX_LENGTH) {
    return `${label} ne peut pas dépasser ${WORD_RICH_TEXT_MAX_LENGTH} caractères.`;
  }
  if (getUtf8Bytes(value) > maxBytes) {
    return `${label} est trop long.`;
  }
  return null;
}

export function validateMotsCaches(raw, mot) {
  const list = Array.isArray(raw) ? raw : [];
  const unique = new Set();
  for (const entry of list) {
    const value = sanitizeMotKey(entry);
    if (!value || isReservedMot(value) || value.length > WORD_CACHE_MAX_LENGTH) {
      continue;
    }
    if (visibleLength(value) < 1) continue;
    unique.add(value);
  }
  if (unique.size < WORD_CACHES_MIN || unique.size > WORD_CACHES_MAX) {
    return `Vous devez fournir entre ${WORD_CACHES_MIN} et ${WORD_CACHES_MAX} mots cachés.`;
  }
  const values = sanitizeMotsCaches(raw);
  const base = sanitizeMotKey(mot);
  if (base && !values.some((entry) => entry === base)) {
    return "Le mot doit figurer parmi les mots cachés.";
  }
  return null;
}

export function validateWordFicheFields({
  mot,
  auteur,
  livre,
  definition,
  extrait,
  motsCaches,
  includeMot = false,
} = {}) {
  if (includeMot) {
    const motError = validateWordMot(mot);
    if (motError) return motError;
  }
  return (
    validateWordLabel(auteur, "de l'auteur", WORD_AUTEUR_MAX_LENGTH) ||
    validateWordLabel(livre, "du livre", WORD_LIVRE_MAX_LENGTH) ||
    validateWordRichText(definition, "La définition", WORD_DEFINITION_MAX_BYTES) ||
    validateWordRichText(extrait, "L’extrait", WORD_EXCERPT_MAX_BYTES) ||
    validateMotsCaches(motsCaches, mot)
  );
}

export function sanitizeWordFiche(word) {
  if (!word || typeof word !== "object" || Array.isArray(word)) return word;
  if (isReservedMot(word.mot)) return word;

  const next = { ...word };
  if (word.mot != null) next.mot = sanitizeMotKey(word.mot);
  if (word._id != null) next._id = sanitizeMotKey(word._id) || next.mot;
  if (word.auteur != null) next.auteur = sanitizeWordLabel(word.auteur);
  if (word.livre != null) next.livre = sanitizeWordLabel(word.livre);
  if (word.definition != null) next.definition = sanitizeWordRichText(word.definition);
  if (word.extrait != null) next.extrait = sanitizeWordRichText(word.extrait);
  if (word.motsCaches != null) next.motsCaches = sanitizeMotsCaches(word.motsCaches);
  return next;
}

export function prepareWordFicheWrite(raw = {}, { includeMot = false } = {}) {
  const mot = includeMot ? sanitizeMotKey(raw.mot) : undefined;
  const fiche = {
    auteur: sanitizeWordLabel(raw.auteur),
    livre: sanitizeWordLabel(raw.livre),
    definition: sanitizeWordRichText(raw.definition),
    extrait: sanitizeWordRichText(raw.extrait),
    motsCaches: sanitizeMotsCaches(raw.motsCaches),
  };
  if (includeMot) fiche.mot = mot;
  return fiche;
}
